import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { Button } from '@/components/ui/button';
import {
  BookOpen, Star, ChevronLeft, ChevronRight, Volume2, VolumeX,
  Sparkles, Award, CheckCircle, XCircle, HelpCircle, Loader2,
  ArrowRight, Search, Lightbulb, Brain, Heart, Trophy, Target
} from 'lucide-react';
import { toast } from 'sonner';

const SKILL_ICONS = {
  prediction: Target,
  inference: Search,
  empathy: Heart,
  'problem-solving': Brain,
};

const ReadingStory = () => {
  const { storyId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();

  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chapterIndex, setChapterIndex] = useState(0);
  const [phase, setPhase] = useState('reading'); // reading, comprehension, challenge, choices, reward, complete
  const [loadingChoice, setLoadingChoice] = useState(false);
  const [playing, setPlaying] = useState(false);

  // Comprehension
  const [compAnswers, setCompAnswers] = useState({});
  const [compResults, setCompResults] = useState(null);
  const [submittingComp, setSubmittingComp] = useState(false);

  // Challenge
  const [challengeAnswer, setChallengeAnswer] = useState('');
  const [challengeResult, setChallengeResult] = useState(null);
  const [submittingChallenge, setSubmittingChallenge] = useState(false);

  // Rewards popup
  const [newBadges, setNewBadges] = useState([]);
  const [starsJustEarned, setStarsJustEarned] = useState(0);

  const fetchStory = useCallback(async () => {
    try {
      const res = await api.get(`/reading/story/${storyId}`);
      setStory(res.data);
      // Set to last chapter
      const chapters = res.data.chapters || [];
      setChapterIndex(chapters.length - 1);
      // Determine phase
      if (res.data.status === 'completed') {
        setPhase('complete');
      } else {
        setPhase('reading');
      }
    } catch (err) {
      toast.error('Failed to load story');
      navigate('/student/reading');
    } finally {
      setLoading(false);
    }
  }, [api, storyId, navigate]);

  useEffect(() => { fetchStory(); }, [fetchStory]);

  const currentChapter = story?.chapters?.[chapterIndex];
  const isLastChapter = currentChapter?.chapter_number === 5;
  const isLatestChapter = chapterIndex === (story?.chapters?.length || 0) - 1;

  const handleTTS = async (text) => {
    if (playing) return;
    setPlaying(true);
    try {
      const res = await api.post('/v2/media/tts/generate', { text: text, voice: 'fable' });
      const audio = new Audio(`data:audio/mp3;base64,${res.data.audio}`);
      audio.onended = () => setPlaying(false);
      audio.onerror = () => setPlaying(false);
      audio.play();
    } catch {
      toast.error('Audio unavailable');
      setPlaying(false);
    }
  };

  const handleChoice = async (choiceId) => {
    setLoadingChoice(true);
    try {
      const res = await api.post('/reading/next-chapter', {
        story_id: storyId,
        choice_id: choiceId,
      });
      setStory(res.data);
      const chapters = res.data.chapters || [];
      setChapterIndex(chapters.length - 1);
      setPhase('reading');
      setCompAnswers({});
      setCompResults(null);
      setChallengeAnswer('');
      setChallengeResult(null);

      // Check for completion rewards
      if (res.data.stars_just_earned > 0) {
        setStarsJustEarned(res.data.stars_just_earned);
      }
      if (res.data.new_badges?.length > 0) {
        setNewBadges(res.data.new_badges);
      }
      if (res.data.status === 'completed') {
        setPhase('complete');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to continue story');
    } finally {
      setLoadingChoice(false);
    }
  };

  const handleSubmitComprehension = async () => {
    const answers = Object.entries(compAnswers).map(([qid, ans]) => ({
      question_id: qid, answer: ans
    }));
    if (answers.length === 0) {
      toast.error('Please answer at least one question!');
      return;
    }
    setSubmittingComp(true);
    try {
      const res = await api.post('/reading/answer-comprehension', {
        story_id: storyId,
        chapter_number: currentChapter.chapter_number,
        answers,
      });
      setCompResults(res.data);
      if (res.data.stars_earned > 0) {
        setStarsJustEarned(prev => prev + res.data.stars_earned);
      }
      if (res.data.new_badges?.length > 0) {
        setNewBadges(prev => [...prev, ...res.data.new_badges]);
      }
    } catch {
      toast.error('Failed to submit answers');
    } finally {
      setSubmittingComp(false);
    }
  };

  const handleSubmitChallenge = async () => {
    if (!challengeAnswer.trim()) return;
    setSubmittingChallenge(true);
    try {
      const res = await api.post('/reading/complete-challenge', {
        story_id: storyId,
        chapter_number: currentChapter.chapter_number,
        answer: challengeAnswer,
      });
      setChallengeResult(res.data);
      if (res.data.stars_earned > 0) {
        setStarsJustEarned(prev => prev + res.data.stars_earned);
      }
      if (res.data.new_badges?.length > 0) {
        setNewBadges(prev => [...prev, ...res.data.new_badges]);
      }
    } catch {
      toast.error('Failed to submit challenge');
    } finally {
      setSubmittingChallenge(false);
    }
  };

  if (loading) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
          <span className="ml-3 text-slate-600">Loading your story...</span>
        </div>
      </StudentLayout>
    );
  }

  if (!story || !currentChapter) {
    return (
      <StudentLayout>
        <div className="text-center py-24">
          <p className="text-slate-500">Story not found</p>
          <Button onClick={() => navigate('/student/reading')} className="mt-4">Back to Reading</Button>
        </div>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout>
      <div data-testid="reading-story-page" className="max-w-3xl mx-auto space-y-6">
        {/* Story Header */}
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/student/reading')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400">Chapter {currentChapter.chapter_number} of 5</span>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map(n => (
                <div
                  key={n}
                  className={`w-6 h-1.5 rounded-full cursor-pointer transition-all ${
                    n <= (story.chapters?.length || 0) ? 'bg-purple-500' :
                    n === (story.chapters?.length || 0) + 1 ? 'bg-purple-200' : 'bg-slate-200'
                  }`}
                  onClick={() => { if (n <= story.chapters.length) { setChapterIndex(n - 1); setPhase('reading'); } }}
                />
              ))}
            </div>
            <div className="flex items-center gap-1 text-yellow-500">
              <Star className="w-4 h-4" />
              <span className="text-sm font-bold">{story.stars_earned || 0}</span>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-slate-800" data-testid="story-title">{story.title}</h1>
          <p className="text-sm text-slate-500 capitalize">{story.theme} adventure with {story.character?.name}</p>
        </div>

        {/* Chapter Navigation */}
        {story.chapters.length > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button
              data-testid="prev-chapter-btn"
              onClick={() => { setChapterIndex(Math.max(0, chapterIndex - 1)); setPhase('reading'); }}
              disabled={chapterIndex === 0}
              className="p-1 rounded-lg hover:bg-slate-100 disabled:opacity-30"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm font-semibold text-slate-600">Chapter {currentChapter.chapter_number}</span>
            <button
              data-testid="next-chapter-btn"
              onClick={() => { setChapterIndex(Math.min(story.chapters.length - 1, chapterIndex + 1)); setPhase('reading'); }}
              disabled={chapterIndex >= story.chapters.length - 1}
              className="p-1 rounded-lg hover:bg-slate-100 disabled:opacity-30"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* READING PHASE */}
        {phase === 'reading' && (
          <div className="space-y-6">
            {/* Story Text */}
            <div data-testid="chapter-text" className="bg-white rounded-2xl border-2 border-slate-200 p-6 md:p-8 relative">
              {/* TTS Button */}
              <button
                data-testid="tts-btn"
                onClick={() => handleTTS(currentChapter.text)}
                disabled={playing}
                className="absolute top-4 right-4 p-2 rounded-lg bg-purple-50 hover:bg-purple-100 text-purple-600 transition-colors"
              >
                {playing ? <Volume2 className="w-5 h-5 animate-pulse" /> : <Volume2 className="w-5 h-5" />}
              </button>

              {/* Chapter text with proper paragraphs */}
              <div className="prose prose-slate max-w-none text-base leading-relaxed">
                {currentChapter.text.split('\n\n').map((para, i) => (
                  <p key={i} className="mb-4 last:mb-0 text-slate-700">{para.trim()}</p>
                ))}
              </div>
            </div>

            {/* Vocabulary */}
            {currentChapter.vocabulary?.length > 0 && (
              <div data-testid="vocabulary-section" className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h3 className="font-semibold text-blue-800 text-sm mb-2 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" /> New Words
                </h3>
                <div className="grid md:grid-cols-2 gap-2">
                  {currentChapter.vocabulary.map((v, i) => (
                    <div key={i} className="flex items-start gap-2 bg-white rounded-lg px-3 py-2 border border-blue-100">
                      <span className="font-bold text-blue-700 text-sm">{v.word}</span>
                      <span className="text-slate-600 text-sm">— {v.definition}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 justify-center">
              {currentChapter.comprehension_questions?.length > 0 && (
                <Button
                  data-testid="comprehension-btn"
                  onClick={() => setPhase('comprehension')}
                  variant="outline"
                  className="gap-2 border-purple-200 text-purple-700 hover:bg-purple-50"
                >
                  <Brain className="w-4 h-4" /> Answer Questions
                </Button>
              )}
              {currentChapter.mini_challenge && (
                <Button
                  data-testid="challenge-btn"
                  onClick={() => setPhase('challenge')}
                  variant="outline"
                  className="gap-2 border-amber-200 text-amber-700 hover:bg-amber-50"
                >
                  <Search className="w-4 h-4" /> Mini Challenge
                </Button>
              )}
              {isLatestChapter && currentChapter.choices?.length > 0 && (
                <Button
                  data-testid="choices-btn"
                  onClick={() => setPhase('choices')}
                  className="gap-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white"
                >
                  <Sparkles className="w-4 h-4" /> Make Your Choice
                </Button>
              )}
            </div>
          </div>
        )}

        {/* COMPREHENSION PHASE */}
        {phase === 'comprehension' && (
          <div className="space-y-4">
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-5">
              <h3 className="font-heading font-bold text-purple-800 mb-4 flex items-center gap-2">
                <Brain className="w-5 h-5" /> Comprehension Questions
              </h3>
              {!compResults ? (
                <div className="space-y-4">
                  {currentChapter.comprehension_questions?.map((q, i) => {
                    const SkillIcon = SKILL_ICONS[q.type] || HelpCircle;
                    return (
                      <div key={q.id} className="bg-white rounded-lg p-4 border border-purple-100">
                        <div className="flex items-start gap-2 mb-2">
                          <SkillIcon className="w-4 h-4 text-purple-500 mt-0.5 shrink-0" />
                          <p className="text-sm font-medium text-slate-700">{q.question}</p>
                        </div>
                        <span className="text-xs text-purple-500 capitalize mb-2 inline-block">{q.type}</span>
                        <textarea
                          data-testid={`comp-answer-${q.id}`}
                          value={compAnswers[q.id] || ''}
                          onChange={e => setCompAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                          placeholder="Write your answer here..."
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none h-20 focus:border-purple-400 outline-none"
                        />
                      </div>
                    );
                  })}
                  <div className="flex gap-3">
                    <Button
                      data-testid="submit-comprehension-btn"
                      onClick={handleSubmitComprehension}
                      disabled={submittingComp}
                      className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
                    >
                      {submittingComp ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                      Submit Answers
                    </Button>
                    <Button variant="outline" onClick={() => setPhase('reading')}>Back to Story</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-center mb-4">
                    <p className="text-lg font-bold text-purple-800">{compResults.correct_count}/{compResults.total} correct!</p>
                    <p className="text-sm text-purple-600">{compResults.encouragement}</p>
                    {compResults.stars_earned > 0 && (
                      <div className="flex items-center justify-center gap-1 mt-2 text-yellow-500">
                        <Star className="w-5 h-5" />
                        <span className="font-bold">+{compResults.stars_earned} stars!</span>
                      </div>
                    )}
                  </div>
                  {compResults.results?.map((r, i) => (
                    <div key={i} className={`p-3 rounded-lg border ${r.correct ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                      <div className="flex items-start gap-2">
                        {r.correct ? <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5" /> : <XCircle className="w-4 h-4 text-amber-600 mt-0.5" />}
                        <div>
                          <p className="text-sm font-medium text-slate-700">{r.question}</p>
                          <p className="text-xs text-slate-500 mt-1">Your answer: {r.your_answer}</p>
                          {!r.correct && r.expected_answer && (
                            <p className="text-xs text-amber-700 mt-1">Suggested: {r.expected_answer}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button variant="outline" onClick={() => setPhase('reading')} className="mt-3">Back to Story</Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CHALLENGE PHASE */}
        {phase === 'challenge' && currentChapter.mini_challenge && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <h3 className="font-heading font-bold text-amber-800 mb-3 flex items-center gap-2">
              <Search className="w-5 h-5" /> Mini Challenge
            </h3>
            <p className="text-xs text-amber-600 capitalize mb-2">Type: {currentChapter.mini_challenge.type?.replace(/_/g, ' ')}</p>
            <p className="text-sm text-slate-700 mb-4 font-medium">{currentChapter.mini_challenge.prompt}</p>

            {!challengeResult ? (
              <div className="space-y-3">
                <input
                  data-testid="challenge-answer-input"
                  value={challengeAnswer}
                  onChange={e => setChallengeAnswer(e.target.value)}
                  placeholder="Type your answer..."
                  className="w-full px-4 py-2.5 border-2 border-amber-200 rounded-xl text-sm focus:border-amber-400 outline-none"
                />
                <div className="flex gap-3">
                  <Button
                    data-testid="submit-challenge-btn"
                    onClick={handleSubmitChallenge}
                    disabled={submittingChallenge || !challengeAnswer.trim()}
                    className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
                  >
                    {submittingChallenge ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    Submit
                  </Button>
                  <Button variant="outline" onClick={() => setPhase('reading')}>Skip</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className={`p-4 rounded-lg ${challengeResult.correct ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {challengeResult.correct ? <CheckCircle className="w-5 h-5 text-emerald-600" /> : <XCircle className="w-5 h-5 text-red-500" />}
                    <span className="font-bold text-sm">{challengeResult.feedback}</span>
                  </div>
                  {challengeResult.stars_earned > 0 && (
                    <div className="flex items-center gap-1 text-yellow-500">
                      <Star className="w-4 h-4" /> <span className="text-sm font-bold">+{challengeResult.stars_earned} stars!</span>
                    </div>
                  )}
                </div>
                <Button variant="outline" onClick={() => setPhase('reading')}>Back to Story</Button>
              </div>
            )}
          </div>
        )}

        {/* CHOICES PHASE */}
        {phase === 'choices' && isLatestChapter && currentChapter.choices?.length > 0 && (
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="font-heading text-xl font-bold text-slate-800 mb-1">What happens next?</h3>
              <p className="text-sm text-purple-600">Choose wisely — your decision shapes the story!</p>
            </div>
            <div className="space-y-3">
              {currentChapter.choices.map((choice, i) => (
                <button
                  key={choice.id}
                  data-testid={`choice-${choice.id}`}
                  onClick={() => handleChoice(choice.id)}
                  disabled={loadingChoice}
                  className="w-full p-4 bg-white border-2 border-slate-200 rounded-xl text-left hover:border-purple-400 hover:bg-purple-50 transition-all group disabled:opacity-50"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 font-bold text-sm flex items-center justify-center shrink-0 group-hover:bg-purple-200">
                      {String.fromCharCode(65 + i)}
                    </div>
                    <span className="text-sm font-medium text-slate-700 flex-1">{choice.text}</span>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-purple-600" />
                  </div>
                </button>
              ))}
            </div>
            {loadingChoice && (
              <div className="flex items-center justify-center gap-3 py-4">
                <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
                <span className="text-sm text-purple-600 font-medium">Creating the next chapter of your adventure...</span>
              </div>
            )}
            <div className="text-center">
              <Button variant="outline" onClick={() => setPhase('reading')} size="sm">Back to Story</Button>
            </div>
          </div>
        )}

        {/* COMPLETION PHASE */}
        {phase === 'complete' && isLastChapter && (
          <div className="space-y-6">
            {/* Story ending */}
            {currentChapter.moral && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center">
                <Trophy className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
                <h3 className="font-heading text-lg font-bold text-emerald-800 mb-2">Story Complete!</h3>
                <p className="text-sm text-emerald-700 italic mb-3">"{currentChapter.moral}"</p>
                {currentChapter.reflection && (
                  <p className="text-sm text-slate-600">{currentChapter.reflection}</p>
                )}
              </div>
            )}

            {/* Stars & Badges earned */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5 text-center">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Star className="w-8 h-8 text-yellow-500" />
                <span className="font-heading text-3xl font-bold text-yellow-700">{story.stars_earned || 0}</span>
                <span className="text-yellow-600 font-medium">Stars Earned</span>
              </div>
              <p className="text-sm text-slate-500">{story.total_words_read || 0} words read across 5 chapters</p>
            </div>

            {/* New badges from this story */}
            {newBadges.length > 0 && (
              <div data-testid="new-badges" className="bg-purple-50 border border-purple-200 rounded-xl p-5 text-center">
                <Award className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <h3 className="font-heading font-bold text-purple-800 mb-3">New Badge{newBadges.length > 1 ? 's' : ''} Unlocked!</h3>
                <div className="flex flex-wrap justify-center gap-3">
                  {newBadges.map((badge, i) => (
                    <div key={i} className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-200 rounded-xl">
                      <Award className="w-5 h-5 text-yellow-500" />
                      <span className="font-semibold text-purple-800 text-sm">{badge.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col items-center gap-3">
              <Button
                data-testid="new-adventure-btn"
                onClick={() => navigate('/student/reading')}
                className="bg-gradient-to-r from-violet-600 to-purple-600 text-white gap-2 px-6"
              >
                <Sparkles className="w-4 h-4" /> Start a New Adventure
              </Button>
              <Button variant="outline" onClick={() => { setChapterIndex(0); setPhase('reading'); }}>
                Re-read This Story
              </Button>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default ReadingStory;
